<script lang="ts" setup>
import Card from 'primevue/card'
import FormSection from '@/components/forms/FormSection.vue'
import FormInputText from '@/components/inputs/FormInputText.vue'
import FormCalendar from '@/components/inputs/FormCalendar.vue'
import { computed, onMounted, ref } from 'vue'
import { getCustomerList } from '@/api/customers'
import FormDropdown from '@/components/inputs/FormDropdown.vue'
import FormTextArea from '@/components/inputs/FormTextArea.vue'
import { getSupplierList } from '@/api/suppliers'
import type { Supplier } from '@/types/supplier-types'
import {getBatchList} from '@/api/batches'
import { getOrders } from '@/api/orders'
import {formatedDate} from "@/constants";
import {PackingListState} from "@/types/packingList-types";
import type {Customer} from "@/types/customer-types";
import type {Order} from "@/types/order-types";
import {getBatchRoutePath} from "@/utils/batch-navigation";
import type {Batch} from "@/types/batch-types";
import {getProductList} from "@/api/products";
import type {Product} from "@/types/product-types";

const props = defineProps<{
  values: any
  type: 'add' | 'edit' | 'view'
  onSubmit: () => void
  setErrors: (errors: any) => void
}>()
const isViewPage = computed<boolean>(() => props.type === 'view')
const customerList = ref<Customer[]>([])
const suppliers = ref<Supplier[] | undefined>()
const batches = ref<any>([])
const orders = ref<Order[]>([])
const qualities = ref<Product[]>([])
const packingListStatus = computed(() => props.values.packingListState)

const getBatchRoute = (batchId: string) => {
  const batch = batches.value.find((batch: Batch) => batch.id === batchId);
  return getBatchRoutePath(batch);
}

const getQualityName = (id: string) => {
  const quality = qualities.value.find((quality: Product) => quality.id === id);
  return quality?.finishedReference || 'Unknown Quality';
}

onMounted(async () => {
  customerList.value = await getCustomerList()
  suppliers.value = await getSupplierList()
  batches.value = await getBatchList()
  orders.value = await getOrders()
  qualities.value = await getProductList()
})
</script>

<template>
  <form @submit.prevent="onSubmit" class="border-t border-neutral-200/50 !rounded-t-3xl">
    <Card>
      <template #content>
        <div class="">
          <FormSection :title="isViewPage ? '' : 'Packing List'" description="">
            <FormInputText
              :viewOnly="isViewPage"
              label="Packing List Number"
              inputName="packingListId"
              id="packingListId"
              placeholder="Enter packing list number"
            />
            <FormCalendar
              :viewOnly="isViewPage"
              label="Packing List Date"
              inputName="packingListDate"
              id="packingListDate"
              placeholder="Enter packing list date"
              v-model="values.packingListDate"
            />
            <template v-if="values.packingListType === 0">
              <div v-if="isViewPage">
                <label class="text-neutral-500 text-sm mb-1 flex gap-x-1" for="finishingSupplierId">Supplier</label>
                <p class="py-2 font-medium text-[15px] bg-gray-50 rounded-lg px-2">
                  <RouterLink
                      :to="`/suppliers/${values.finishingSupplierId}`"
                      class="font-medium text-neutral-800 hover:underline"
                  >
                    {{ suppliers?.find(s => s.id === values.finishingSupplierId)?.name }}
                  </RouterLink>
                </p>
              </div>
              <FormDropdown
                  v-else
                  label="Supplier"
                  inputName="finishingSupplierId"
                  id="finishingSupplierId"
                  :options="suppliers"
                  optionLabel="name"
                  optionValue="id"
                  :viewOnly="isViewPage"
              />
              <div v-if="isViewPage">
                <label class="text-neutral-500 text-sm mb-1 flex gap-x-1" for="batchId">Batch</label>
                <p class="py-2 font-medium text-[15px] bg-gray-50 rounded-lg px-2">
                  <RouterLink
                      :to="getBatchRoute(values.batchId)"
                      class="font-medium text-neutral-800 hover:underline"
                  >
                    {{ batches?.find((b: Batch) => b.id === values.batchId)?.batchNumber }}
                  </RouterLink>
                </p>
              </div>
              <FormDropdown
                  v-else
                  label="Batch"
                  inputName="batchId"
                  id="batchId"
                  :options="batches"
                  optionLabel="batchNumber"
                  optionValue="id"
                  :viewOnly="isViewPage"
              />
            </template>
            <template v-if="values.packingListType === 1">
              <div v-if="isViewPage">
                <label class="text-neutral-500 text-sm mb-1 flex gap-x-1" for="customerId">Customer</label>
                <p class="py-2 font-medium text-[15px] bg-gray-50 rounded-lg px-2">
                  <RouterLink
                      :to="`/customers/${values.customerId}`"
                      class="font-medium text-neutral-800 hover:underline"
                  >
                    {{ customerList?.find(c => c.id === values.customerId)?.businessName }}
                  </RouterLink>
                </p>
              </div>
              <FormDropdown
                  v-else
                  label="Customer"
                  inputName="customerId"
                  id="customerId"
                  :options="customerList"
                  optionLabel="businessName"
                  optionValue="id"
                  :viewOnly="isViewPage"
              />
            </template>
            <template v-if="values.packingListType === 1">
              <div v-if="isViewPage">
                <label class="text-neutral-500 text-sm mb-1 flex gap-x-1" for="orderId">Order Number</label>
                <p class="py-2 font-medium text-[15px] bg-gray-50 rounded-lg px-2">
                  <RouterLink
                      :to="`/orders/${values.orderId}`"
                      class="font-medium text-neutral-800 hover:underline"
                  >
                    {{ orders?.find(o => o.id === values.orderId)?.orderNumber }}
                  </RouterLink>
                </p>
              </div>
              <FormDropdown
                  v-else
                  label="Order Number"
                  inputName="orderId"
                  id="orderId"
                  :options="orders"
                  optionLabel="orderNumber"
                  optionValue="id"
                  :viewOnly="isViewPage"
                  placeholder="choose order"
              />
            </template>
            <FormCalendar
                v-if="packingListStatus === PackingListState.Dispatched || packingListStatus === PackingListState.Delivered"
                :viewOnly="isViewPage"
                label="Dispatch Date"
                inputName="dispatchDate"
                id="dispatchDate"
                placeholder="Enter dispatch date"
                v-model="values.dispatchDate"
            />
            <FormInputText
                v-if="packingListStatus === PackingListState.Dispatched || packingListStatus === PackingListState.Delivered"
                :viewOnly="isViewPage"
                label="Dispatched By"
                inputName="dispatchEnteredBy"
                id="dispatchEnteredBy"
                placeholder="Enter name of dispatcher"
                v-model="values.dispatchEnteredBy"
            />
            <FormCalendar
                v-if="packingListStatus === PackingListState.Delivered"
                :viewOnly="isViewPage"
                label="Delivery Date"
                inputName="deliveryDate"
                id="deliveryDate"
                placeholder="Enter delivery date"
                v-model="values.deliveryDate"
            />
            <FormInputText
                v-if="packingListStatus === PackingListState.Delivered"
                :viewOnly="isViewPage"
                label="Delivered By"
                inputName="deliveryEnteredBy"
                id="deliveryEnteredBy"
                placeholder="Enter name of delivery person"
                v-model="values.deliveryEnteredBy"
            />
            <div v-if="isViewPage && values.qualityId">
              <label class="text-neutral-500 text-sm mb-1 flex gap-x-1" for="qualityId">Quality</label>
              <p class="py-2 font-medium text-[15px] bg-gray-50 rounded-lg px-2">
                <RouterLink
                    :to="`/productSpecification/${values.qualityId}`"
                    class="font-medium text-neutral-800 hover:underline"
                >
                  {{ getQualityName(values.qualityId) }}
                </RouterLink>
              </p>
            </div>
            <FormTextArea label="Notes" inputName="notes" id="notes" />
          </FormSection>
        </div>
      </template>
    </Card>
  </form>
</template>
